import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IAppRole,
  IEmployeeCurrentUserInfo,
  IEmployeeFull,
  IOption,
  IUserData,
  IUserRequest,
  IEmployeeShort
} from '@common/types';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { AppRolesEnum } from '@common/enums';
import { IUpdateEmployee } from '@common/dialogs';
import { omit } from 'lodash-es';
import { optionMapper } from '@common/modules/committees/committees-form/utils/adaptors';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {
  private readonly employeeCurrentUserInfo = new BehaviorSubject<IEmployeeCurrentUserInfo>(null);
  private positions = new BehaviorSubject<IOption[]>([]);

  constructor(private readonly _http: HttpClient) {
    this.retrieveEmployeeCurrentUserInfo().subscribe((user) => {
      this.employeeCurrentUserInfo.next(user);
    });
    this.initPositions();
  }

  get positions$(): Observable<IOption[]> {
    return this.positions.asObservable();
  }

  private initPositions(): void {
    this.getPositions('', 10000)
      .pipe(
        tap((positions) => {
          this.positions.next(positions.map(optionMapper).filter(Boolean));
        })
      )
      .subscribe();
  }

  public updateCurrentUser(user: Partial<IEmployeeCurrentUserInfo>): void {
    this.employeeCurrentUserInfo.next({
      ...this.employeeCurrentUserInfo.value,
      ...user
    });
  }

  public getCurrentUserId(): string {
    return this.employeeCurrentUserInfo.value.id;
  }

  get employeeCurrentUserInfo$(): Observable<IEmployeeCurrentUserInfo> {
    return this.employeeCurrentUserInfo.asObservable();
  }

  public currentUserIsAdmin(): boolean {
    return this.employeeCurrentUserInfo.getValue()?.appRole.keyCloakName === AppRolesEnum.ADMIN;
  }

  public retrieveAppRoles(): Observable<IOption[]> {
    return this._http.get<IAppRole[]>('api/AppRole');
  }

  public getPositions(name = ' ', pageSize = 25): Observable<string[]> {
    const params = new HttpParams().set('name', name).set('page', 0).set('pageSize', pageSize);
    return this._http.get<string[]>('api/Position/forEmployees', { params });
  }

  public retrieveEmployeeById(employeeId: string): Observable<IEmployeeFull> {
    return this._http.get<IEmployeeFull>(`api/Employee/${employeeId}`);
  }

  public retrieveEmployeeByAdmin(options?: IUserRequest): Observable<IUserData> {
    let params = new HttpParams().set('page', options?.page || 0).set('pageSize', options?.pageSize || 10);
    if (options?.position) {
      params = params.append('position', options.position);
    }
    if (options?.appRoleId) {
      params = params.append('appRoleId', options.appRoleId);
    }
    if (options?.department) {
      params = params.append('department', options.department);
    }
    if (options?.name) {
      params = params.append('name', options.name);
    }

    return this._http.get<IUserData>('api/Employee/employeeByAdmin', { params });
  }

  public retrieveEmployeeSearchForSelect(search = '', page = 0, pageSize = 30): Observable<IEmployeeShort[]> {
    return this._http.post<IEmployeeShort[]>('api/Employee/searchForSelect', {
      search,
      page,
      pageSize
    });
  }

  public retrieveEmployeeCurrentUserInfo(): Observable<IEmployeeCurrentUserInfo> {
    return this._http.get<IEmployeeCurrentUserInfo>('api/Employee/currentUserInfo').pipe(
      map((res) => ({
        ...res,
        fullName: `${res.lastName} ${res.name} ${res.patronymic.charAt(0)}`,
        shortName: `${res.lastName} ${res.name.charAt(0)} ${res.patronymic.charAt(0)}`
      }))
    );
  }

  public updateEmployee(body: IUpdateEmployee): Observable<void> {
    return this._http.put<void>(`api/Employee/${body.employeeId}/UpdateEmployee`, omit(body, 'employeeId'));
  }

  public activateEmployee(id: string): Observable<void> {
    return this._http.post<void>(`api/Employee/${id}/activate`, null);
  }

  public deactivateEmployee(id: string): Observable<void> {
    return this._http.post<void>(`api/Employee/${id}/deactivate`, null);
  }
}
